// Deelnemers per editie en het sportersregister van de organisatie.
//
// Vullen gaat via de import 'Deelnemers' (Sportdata competitor entries):
// src/lib/import-deelnemers.js zet de export om en laat gevoelige kolommen
// weg, public.deelnemers_importeren doet eerst een proef en schrijft pas na
// bevestiging. Een sporter staat één keer in het register, met per editie
// zijn deelnames; zo is te zien wie aan welke toernooien meedeed.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { tabelTeken } from '../ui/tabel.js';
import { refLabel } from '../ui/ref.js';
import { leesDeelnemers, WEGGELATEN } from '../lib/import-deelnemers.js';

const sb = () => getClient();
let toonAfgemeld = false;
let klaarVoorImport = null;   // { event, rijen } na de proef

function datum(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
export function leeftijdOp(geboren, peil) {
  if (!geboren) return null;
  const g = new Date(geboren + 'T00:00:00Z');
  const p = peil ? new Date(peil + 'T00:00:00Z') : new Date();
  let jaren = p.getUTCFullYear() - g.getUTCFullYear();
  if (p.getUTCMonth() < g.getUTCMonth() || (p.getUTCMonth() === g.getUTCMonth() && p.getUTCDate() < g.getUTCDate())) jaren--;
  return jaren;
}
export function graadTekst(r) {
  if (r.dan) return t('dln.graad.dan', { n: r.dan });
  if (r.gup) return t('dln.graad.gup', { n: r.gup });
  return '';
}
function fout(e) {
  const m = String(e?.message || e || '').match(/deelnemers:([a-z_]+)/);
  toast(m ? t('dln.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) }), 'error');
}
const kpi = (label, waarde) => `<div class="kpi-card"><div class="kpi-label">${esc(label)}</div><div class="kpi-value">${esc(String(waarde))}</div></div>`;

// ── Modal ──────────────────────────────────────────────────────────────────
function modal(titel, lichaam, voet) {
  let m = document.getElementById('dlnModal');
  if (!m) {
    m = document.createElement('div');
    m.className = 'modal-overlay';
    m.id = 'dlnModal';
    m.innerHTML = '<div class="modal" style="max-width:720px"><div id="dlnModalInhoud" style="display:flex;flex-direction:column;min-height:0;flex:1"></div></div>';
    document.body.appendChild(m);
    m.addEventListener('click', e => {
      if (e.target === m || e.target.closest('[data-dln="sluit"]')) { m.classList.remove('open'); return; }
      if (e.target.closest('[data-dln="bevestig"]')) voerUit();
    });
  }
  m.querySelector('#dlnModalInhoud').innerHTML = `
    <div class="modal-header"><span class="modal-title">${esc(titel)}</span>
      <button type="button" class="btn-icon" data-dln="sluit" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
    <div class="modal-body">${lichaam}</div>
    ${voet ? `<div class="modal-footer">${voet}</div>` : ''}`;
  m.classList.add('open');
}
const sluitModal = () => document.getElementById('dlnModal')?.classList.remove('open');

// ── Deelnemers van de editie ───────────────────────────────────────────────
export async function renderDeelnemers() {
  const el = document.getElementById('deelnemersInhoud');
  if (!el) return;
  if (!el.dataset.gekoppeld) {
    el.dataset.gekoppeld = '1';
    el.addEventListener('change', e => { if (e.target.matches('[data-dln="afgemeld"]')) { toonAfgemeld = e.target.checked; renderDeelnemers(); } });
    el.addEventListener('click', e => { if (e.target.closest('[data-dln="import"]')) document.getElementById('csvDeelnemersInput')?.click(); });
  }
  if (!state.currentEventId) { el.innerHTML = `<p style="color:var(--text-3)">${esc(t('dln.geeneditie'))}</p>`; return; }
  el.innerHTML = '<div class="spinner"></div>';
  const { data, error } = await sb().from('deelnemers_overzicht').select('*').eq('event_id', state.currentEventId).order('achternaam');
  if (error) { el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('dln.nietbeschikbaar'))}</span></div>`; return; }
  const alle = data || [];
  const actief = alle.filter(r => r.status === 'ingeschreven');
  const rijen = toonAfgemeld ? alle : actief;
  const peil = state.events.find(e => e.id === state.currentEventId)?.tournament_date || null;
  const sporters = new Set(actief.map(r => r.athlete_id));
  const jong = new Set(actief.filter(r => (leeftijdOp(r.geboortedatum, peil) ?? 99) < 18).map(r => r.athlete_id));
  el.innerHTML = `
    <div class="kpi-bar" style="margin:0 0 16px">
      ${kpi(t('dln.kpi.sporters'), sporters.size)}
      ${kpi(t('dln.kpi.deelnames'), actief.length)}
      ${kpi(t('dln.kpi.clubs'), new Set(actief.map(r => r.club_sportdata_id)).size)}
      ${kpi(t('dln.kpi.landen'), new Set(actief.map(r => r.club_land).filter(Boolean)).size)}
      ${kpi(t('dln.kpi.minderjarig'), jong.size)}
    </div>
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px;flex-wrap:wrap">
      ${heeft('data.write') ? `<button type="button" class="btn btn-primary btn-sm" data-dln="import"><i class="ti ti-upload"></i> ${esc(t('dln.import'))}</button>` : ''}
      <label style="font-size:13px;display:flex;align-items:center;gap:6px"><input type="checkbox" data-dln="afgemeld" ${toonAfgemeld ? 'checked' : ''}> ${esc(t('dln.toonafgemeld', { aantal: alle.length - actief.length }))}</label>
      <span style="font-size:12px;color:var(--text-3)">${esc(t('dln.uitleg'))}</span>
    </div>
    <div id="dlnTabel"></div>`;
  tabelTeken('deelnemers-editie', 'dlnTabel', [
    { veld: 'naam', label: t('dln.kol.naam'),
      cel: r => `<strong>${esc(r.naam)}</strong>${r.status === 'afgemeld' ? ` <span class="badge badge-grey">${esc(t('dln.afgemeld'))}</span>` : ''}` },
    { veld: 'club_naam', label: t('dln.kol.club') },
    { veld: 'club_land', label: t('dln.kol.land') },
    { veld: 'geslacht', label: t('dln.kol.geslacht'), tekst: r => r.geslacht ? t('dln.geslacht.' + r.geslacht) : '' },
    { veld: 'geboortedatum', label: t('dln.kol.leeftijd'), uitlijn: 'right', mono: true, filter: false,
      tekst: r => String(leeftijdOp(r.geboortedatum, peil) ?? ''), cel: r => esc(String(leeftijdOp(r.geboortedatum, peil) ?? '')) },
    { veld: 'onderdeel', label: t('dln.kol.onderdeel'), tekst: r => t('dossier.onderdeel.' + r.onderdeel) },
    { veld: 'categorie', label: t('dln.kol.categorie') },
    { veld: 'graad', label: t('dln.kol.graad'), tekst: graadTekst },
  ], rijen, { zoekLabel: t('dln.zoek'), leeg: t('dln.leeg'), sorteerVeld: 'naam', rijKlik: r => openSporter(r.athlete_id) });
}

// ── Sportersregister ───────────────────────────────────────────────────────
export async function renderSporters() {
  const el = document.getElementById('sportersInhoud');
  if (!el) return;
  el.innerHTML = '<div class="spinner"></div>';
  const { data, error } = await sb().from('sporters_overzicht').select('*').eq('organisation_id', state.currentOrgId).order('achternaam');
  if (error) { el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('dln.nietbeschikbaar'))}</span></div>`; return; }
  const rijen = data || [];
  el.innerHTML = `
    <div class="kpi-bar" style="margin:0 0 16px">
      ${kpi(t('dln.kpi.sporters'), rijen.length)}
      ${kpi(t('dln.kpi.terugkerend'), rijen.filter(r => Number(r.edities) > 1).length)}
    </div>
    <p style="font-size:12px;color:var(--text-3);margin-bottom:12px">${esc(t('dln.register.uitleg'))}</p>
    <div id="sprTabel"></div>`;
  tabelTeken('sporters', 'sprTabel', [
    { veld: 'naam', label: t('dln.kol.naam'), cel: r => `<strong>${esc(r.naam)}</strong>` },
    { veld: 'ref', label: t('ref.kol'), mono: true, cel: r => refLabel(r.ref) },
    { veld: 'club_naam', label: t('dln.kol.laatsteclub') },
    { veld: 'nationaliteit', label: t('dln.kol.nationaliteit') },
    { veld: 'nationaal_id', label: t('dln.kol.nationaalid'), mono: true },
    { veld: 'edities', label: t('dln.kol.edities'), uitlijn: 'right', mono: true, filter: false },
    { veld: 'laatste_editie', label: t('dln.kol.laatste'), filter: false, tekst: r => r.laatste_editie || '', cel: r => esc(datum(r.laatste_editie)) },
  ], rijen, { zoekLabel: t('dln.zoek'), leeg: t('dln.register.leeg'), sorteerVeld: 'naam', rijKlik: r => openSporter(r.id) });
}

async function openSporter(id) {
  const [s, h] = await Promise.all([
    sb().from('sporters_overzicht').select('*').eq('id', id).maybeSingle(),
    sb().rpc('sporter_historie', { p_sporter: id }),
  ]);
  if (s.error || !s.data) { fout(s.error); return; }
  const p = s.data;
  const regels = (h.data || []).map(r => `<div class="org-row" style="gap:8px;flex-wrap:wrap">
      <div style="flex:1;min-width:200px;font-size:13px"><strong>${esc(r.toernooi || '')}</strong> ${esc(r.editie || '')}
        <div style="font-size:12px;color:var(--text-2)">${esc([datum(r.datum), r.club_naam].filter(Boolean).join(' · '))}</div></div>
      <div style="font-size:12px">${esc(t('dossier.onderdeel.' + r.onderdeel))} · ${esc(r.categorie)}
        ${r.status === 'afgemeld' ? ` <span class="badge badge-grey">${esc(t('dln.afgemeld'))}</span>` : ''}</div>
    </div>`).join('');
  modal(p.naam, `
    <div style="font-size:13px;color:var(--text-2);margin-bottom:10px">${refLabel(p.ref)}
      ${esc([p.nationaliteit, p.geslacht ? t('dln.geslacht.' + p.geslacht) : '', datum(p.geboortedatum), p.club_naam].filter(Boolean).join(' · '))}</div>
    ${p.email || p.nationaal_id || p.internationaal_id ? `<div style="font-size:12px;color:var(--text-2);margin-bottom:10px">${[
      p.email ? `<i class="ti ti-mail"></i> <a href="mailto:${esc(p.email)}">${esc(p.email)}</a>` : '',
      p.nationaal_id ? esc(t('dln.nationaalid', { id: p.nationaal_id })) : '',
      p.internationaal_id ? esc(t('dln.internationaalid', { id: p.internationaal_id })) : '',
    ].filter(Boolean).join(' · ')}</div>` : ''}
    <div class="section-title"><i class="ti ti-history"></i> ${esc(t('dln.historie', { aantal: p.edities }))}</div>
    ${regels || `<p style="font-size:12px;color:var(--text-3)">${esc(t('dln.historie.leeg'))}</p>`}`,
    `<span style="margin-left:auto"></span><button type="button" class="btn btn-default" data-dln="sluit">${esc(t('alg.sluiten'))}</button>`);
}

// ── Import ─────────────────────────────────────────────────────────────────
export async function importeer(bestand) {
  if (!bestand) return;
  const event = state.currentEventId;
  if (!event) { toast(t('dln.geeneditie'), 'error'); return; }
  const gelezen = leesDeelnemers(await bestand.text());
  if (gelezen.kopOntbreekt) { toast(t('dln.fout.formaat'), 'error'); return; }
  if (!gelezen.rijen.length) { toast(t('dln.fout.leeg'), 'error'); return; }
  const { data, error } = await sb().rpc('deelnemers_importeren', { p_event: event, p_rijen: gelezen.rijen, p_proef: true });
  if (error) { fout(error); return; }
  klaarVoorImport = { event, rijen: gelezen.rijen };
  const editie = state.events.find(e => e.id === event)?.name || '';
  const weg = WEGGELATEN.filter(k => gelezen.weggelaten[k]).map(k => `${k} (${gelezen.weggelaten[k]})`);
  const lijst = (titel, namen) => namen?.length ? `<div style="font-size:12px;margin-top:8px"><strong>${esc(titel)}</strong><br>${esc(namen.join(', '))}</div>` : '';
  const fouten = [...gelezen.fouten, ...(data?.fouten || [])];
  modal(t('dln.proef.titel', { editie }), `
    <div class="act-bedragen">
      <div><span>${esc(t('dln.proef.sporters_nieuw'))}</span><strong>${data.sporters_nieuw}</strong></div>
      <div><span>${esc(t('dln.proef.sporters_bijgewerkt'))}</span><strong>${data.sporters_bijgewerkt}</strong></div>
      <div><span>${esc(t('dln.proef.deelnames_nieuw'))}</span><strong>${data.deelnames_nieuw}</strong></div>
      <div><span>${esc(t('dln.proef.deelnames_afgemeld'))}</span><strong>${data.deelnames_afgemeld}</strong></div>
    </div>
    <p style="font-size:13px;margin-top:10px">${esc(t('dln.proef.totaal', { sporters: data.sporters_totaal, deelnames: data.deelnames_totaal, clubs: data.clubs_totaal }))}</p>
    ${data.deelnames_afgemeld ? `<div class="banner banner-amber" style="margin-top:8px"><i class="ti ti-alert-triangle"></i><span>${esc(t('dln.proef.afmeldwaarschuwing', { aantal: data.deelnames_afgemeld }))}</span></div>` : ''}
    ${lijst(t('dln.proef.zonderbetaling'), data.clubs_zonder_betaling)}
    ${lijst(t('dln.proef.zonderregister'), data.clubs_zonder_register)}
    ${fouten.length ? `<div style="font-size:12px;margin-top:8px"><strong>${esc(t('dln.proef.fouten', { aantal: fouten.length }))}</strong><br>${esc(fouten.slice(0, 20).map(f => t('dln.rijfout', { rij: f.rij, fout: t('dln.rijfout.' + f.fout) })).join('; '))}</div>` : ''}
    ${weg.length ? `<p style="font-size:12px;color:var(--text-3);margin-top:10px"><i class="ti ti-shield-lock"></i> ${esc(t('dln.proef.weggelaten', { kolommen: weg.join(', ') }))}</p>` : ''}`,
    `<span style="margin-left:auto"></span>
     <button type="button" class="btn btn-default" data-dln="sluit">${esc(t('alg.annuleren'))}</button>
     <button type="button" class="btn btn-primary" data-dln="bevestig"><i class="ti ti-check"></i> ${esc(t('dln.proef.bevestig'))}</button>`);
}

async function voerUit() {
  if (!klaarVoorImport) return;
  const { event, rijen } = klaarVoorImport;
  klaarVoorImport = null;
  const { data, error } = await sb().rpc('deelnemers_importeren', { p_event: event, p_rijen: rijen, p_proef: false });
  if (error) { fout(error); return; }
  sluitModal();
  toast(t('dln.gelukt', { sporters: data.sporters_totaal, deelnames: data.deelnames_totaal }), 'success');
  if (document.getElementById('pageDeelnemers')?.classList.contains('active')) renderDeelnemers();
}
